/**
 * Navigation Key Event Utils
 * Supports vertical and horizontal nav layouts
 * @author thomas.miller@isobar.com
 * @author yauheni.mukavozchyk@isobar.com
 */

import utils from 'utils';
import { findTabbableDescendants } from './tabbable';
import { formatKeys } from './keys';

/**
 * Constants
 */
const IS_VERTICAL = 'is-vertical-nav';
const IS_MOBILE_HORIZONTAL = 'is-mobile-horizontal-nav';
const IS_MOBILE_VERTICAL = 'is-mobile-vertical-nav';

/**
 * TODO
 * @param {DOMElement} domEl
 */
const getElementWithAttribute = (domEl, tagName, attribute) => {
  if (domEl.tagName === tagName && domEl.hasAttribute(attribute)) {
    return domEl;
  }
  const parent = domEl.parentElement;
  return parent !== document.body ? getElementWithAttribute(parent, tagName, attribute) : null;
};

/**
 * Build Props Object
 * @param {Event} e
 * @param {Element} domEl
 * @returns {Object}
 */
export const buildProps = (e, domEl, isVertical) => {
  const tabbable = findTabbableDescendants(domEl);
  const keys = formatKeys(e);
  const currentEl = e.target;
  const currentElIndex = tabbable.indexOf(currentEl);
  const props = {
    keys,
    nav: {
      el: domEl,
      tabbable,
    },
    previous: {
      el: tabbable[currentElIndex - 1],
    },
    current: {
      el: currentEl,
      index: currentElIndex,
      hasPopUp: currentEl.getAttribute('aria-haspopup') === 'true',
      isPopUpExpanded: currentEl.getAttribute('aria-expanded') === 'true',
      isMobileHorizontal: !!getElementWithAttribute(currentEl, 'SECTION', IS_MOBILE_HORIZONTAL),
    },
    next: {
      el: tabbable[currentElIndex + 1],
    },
  };

  // routine for sub menus
  let subMenuEl = currentEl.closest('nav');
  if (['helpToggle', 'languageToggle'].includes(subMenuEl?.getAttribute('data-alamo-ui'))) {
    subMenuEl = currentEl.closest('ol');
  }

  let isInsideSubMenu = subMenuEl ? subMenuEl.hasAttribute(IS_VERTICAL) : false;

  if (!isInsideSubMenu) {
    subMenuEl = getElementWithAttribute(currentEl, 'SECTION', IS_MOBILE_VERTICAL);
    isInsideSubMenu = isVertical && subMenuEl;
  }

  if (isInsideSubMenu) {
    Object.assign(props, {
      subMenu: {
        tabbable: findTabbableDescendants(subMenuEl),
      },
    });
  }

  return props;
};

/**
 * Compare the activeElement to the last element in tabbable array by direction
 * @param {Object} props
 * @returns {Boolean}
 */
const isLeavingMenu = ({ keys, subMenu }) => {
  const isFirstItemBoundary = keys.arrowUp || keys.shiftTab;
  const focusedElement = isFirstItemBoundary ? subMenu.tabbable[0] : subMenu.tabbable[subMenu.tabbable.length - 1];
  return document.activeElement === focusedElement;
};

/**
 * Find parent menu controls, close menu
 * @param {Object} props
 */
const closeParentMenu = ({ current, nav }) => {
  let parentEl = current.el;
  while (parentEl) {
    if (parentEl === nav.el || parentEl === document.body) {
      break;
    }

    if (parentEl.getAttribute('aria-hidden') === 'false' || parentEl?.closest('nav')?.classList?.contains('expanded')) {
      const link =
        nav.el.querySelector(`[aria-controls="${parentEl.id}"]`) ||
        parentEl?.closest('nav').querySelector?.(`[aria-expanded="true"]`);

      if (link) {
        link.click();
        link.focus();
        break;
      }
    }

    parentEl = parentEl.parentNode;
  }
};

/**
 * Key actions routine specific to the horizontal nav
 * @param {Event} e
 * @param {Object} props
 */
const horizontalNavRoutine = (e, props) => {
  const { keys, current, next, previous, subMenu } = props;
  const { hasPopUp, isPopUpExpanded } = current;

  // top-level navigation
  if (!subMenu && keys.arrowLeftOrRight) {
    e.preventDefault();
    if (hasPopUp && isPopUpExpanded) {
      // close sub-menu
      current.el.click();
    } else if (keys.arrowRight) {
      next.el && next.el.focus();
    } else {
      previous.el && previous.el.focus();
    }
  }

  // open sub-menu or navigate down inside of sub-menu
  if (keys.arrowDown) {
    e.preventDefault();
    if (subMenu) {
      // reached last element - close menu; otherwise focus next;
      isLeavingMenu(props) ? closeParentMenu(props) : next.el.focus();
    } else if (hasPopUp) {
      // menu item with sub-menu
      // if sub-menu open -> focus first item of the sub-menu; if not open - open it
      isPopUpExpanded ? next.el.focus() : current.el.click();
    }
  }

  // close the sub-menu or navigate upwards inside of sub-menu
  if (keys.arrowUp) {
    e.preventDefault();
    if (subMenu) {
      // reached first element - close menu; otherwise focus previous;
      isLeavingMenu(props) ? closeParentMenu(props) : previous.el.focus();
    } else if (hasPopUp && isPopUpExpanded) {
      current.el.click();
      current.el.focus();
    }
  }

  // close sub-menu
  if (subMenu && keys.arrowLeftOrRight) {
    e.preventDefault();
    closeParentMenu(props);
  }
};

/**
 * Key actions routine specific to the vertical nav
 * @param {Event} e
 * @param {Object} props
 */
const verticalNavRoutine = (e, props) => {
  const { keys, current, next, previous, subMenu } = props;
  const { hasPopUp, isPopUpExpanded } = current;

  // top-level navigation
  if (keys.arrowUpOrDown) {
    e.preventDefault();
    if (subMenu && isLeavingMenu(props)) {
      // closeParentMenu(props);
      keys.arrowUp ? subMenu.tabbable[subMenu.tabbable.length - 1].focus() : subMenu.tabbable[0].focus();
    } else if (keys.arrowDown) {
      next.el && next.el.focus();
    } else {
      previous.el && previous.el.focus();
    }
  }

  // close sub-menu
  if (keys.arrowLeft) {
    e.preventDefault();
    if (hasPopUp && isPopUpExpanded) {
      current.el.click();
      current.el.focus();
    } else if (subMenu) {
      closeParentMenu(props);
    }
  }

  // open sub-menu
  if (keys.arrowRight) {
    e.preventDefault();
    if (hasPopUp && !isPopUpExpanded) {
      current.el.click();
      // current.el.focus();
    }
  }
};

/**
 * Add additional keyboard actions to navigation
 * @param {Object}
 */
export const navigationKeyEvent = ({ e, domEl, isVertical, isSpaceClick, customHandlers = new Map() }) => {
  const props = buildProps(e, domEl, isVertical);
  const { keys, current, subMenu } = props;
  const { hasPopUp, isPopUpExpanded } = current;

  // generic handlers
  if (isSpaceClick && keys.space) {
    e.preventDefault();
    current.el.click();
  }

  if (subMenu && keys.escape) {
    e.preventDefault();
    closeParentMenu(props);
  }

  if (keys.shiftTab && hasPopUp && isPopUpExpanded) {
    e.preventDefault();
    current.el.click();
  }

  // vertical / horizontal nav routine
  isVertical && !current.isMobileHorizontal ? verticalNavRoutine(e, props) : horizontalNavRoutine(e, props);

  // custom handlers
  // a Map() of custom handlers:
  // - <key> is a string from "keys" object which represents a single key press or combo i.e. "escape" or "shiftTab"
  // - <value> is a handler function which accepts { props, e }
  customHandlers.forEach((value, key) => keys[key] && utils.safeExecute(value, { props, e }));
};
